import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

export function formatMoney(value: string | number | null | undefined): string {
  if (value == null) return '-'
  return moneyFormatter.format(Number(value))
}

const pctFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

export function formatPercent(value: string | number | null | undefined): string {
  if (value == null) return '-'
  return `${pctFormatter.format(Number(value))}%`
}

/**
 * Format a number with a sensible number of decimals, stripping trailing zeros.
 * Rounds to `maxDecimals` (default 2) so values like 27.379999999999999 render as "27.38".
 */
export function formatNumber(
  value: string | number | null | undefined,
  maxDecimals = 2,
): string {
  if (value == null || value === '') return '-'
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  })
}

// Preserve the shipped portal's Z-only suffix handling, including Invalid Date
// for offset timestamps. parseUTCDate has different semantics; do not substitute it.
export function parsePortalDate(iso: string): Date {
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`)
}

/**
 * Parse a date string from the backend as UTC.
 * Backend sends naive datetime strings (no timezone suffix) that are actually UTC.
 * Appends 'Z' if the string has no timezone info so the browser correctly
 * converts to local time when formatting.
 */
export function parseUTCDate(dateStr: string): Date {
  if (dateStr.endsWith('Z') || dateStr.includes('+') || /[-+]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr)
  }
  return new Date(dateStr + 'Z')
}

/**
 * Format a date-only string (YYYY-MM-DD) without timezone conversion.
 * Backend date fields (not datetime) are sent as "2025-12-30". Using parseUTCDate
 * on these appends 'Z', shifting to midnight UTC which displays as the previous
 * day in US timezones. This function parses the components directly to avoid that.
 */
export function formatDateOnly(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const parts = dateStr.split('-').map(Number)
  const y = parts[0] ?? 0
  const m = parts[1] ?? 1
  const d = parts[2] ?? 1
  return new Date(y, m - 1, d).toLocaleDateString()
}

/**
 * Format a follow-up date string (YYYY-MM-DD) as a local datetime string.
 * Follow-ups fire at 9:00 AM CT (15:00 UTC), so we display that time
 * converted to the user's local timezone.
 */
export function formatFollowUpTime(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  // 9 AM CT = 15:00 UTC
  const utc = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1, 15, 0, 0))
  return utc.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}

export function formatStatus(status: string | null | undefined): string {
  if (!status) return '-'
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
